import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { StatusPill } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/dates";
import { CheckSquare, Check, X, Undo2 } from "lucide-react";

export default function Acceptances() {
  const { user, can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [comment, setComment] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/acceptances");
      setItems(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sweep = async () => {
    try { const { data } = await api.post("/acceptances/expire-sweep"); toast.success(`Sweep done — ${data.expired} expired`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const decide = async () => {
    try {
      await api.post(`/acceptances/${decidingId}/decision`, { decision, comment });
      toast.success(`Decision recorded: ${decision}`);
      setDecidingId(null); setComment(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const daysUntil = (iso) => {
    const d = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
    return d;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker="Approvals"
        title="Risk Acceptances"
        description="Requests to accept risks that breach appetite. Approver must differ from requester and owner."
        actions={<Button variant="outline" onClick={sweep} data-testid="btn-expire-sweep"><Undo2 className="h-4 w-4 mr-1.5" /> Run expiry sweep</Button>}
      />
      {loading ? <LoadingSkeleton /> : items.length === 0 ? (
        <EmptyState icon={CheckSquare} title="No acceptance requests" description="Requests from Risk Owners will appear here." />
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const days = daysUntil(a.expires_at);
            const iAmApprover = can("acceptance.approve") && a.approver_id === user.id && a.status === "Pending";
            return (
              <Card key={a.id} className="p-5" data-testid={`row-acc-${a.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900 text-sm">{a.risk_code}</span>
                      <StatusPill status={a.status} />
                      {a.status === "Approved" && (
                        <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded border ${days < 30 ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                          {days > 0 ? `${days}d until expiry` : "expired"}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-800 mt-2 max-w-2xl italic">"{a.justification}"</div>
                    <div className="text-xs text-slate-500 mt-2 font-mono">
                      Requester: <span className="text-slate-800 font-semibold">{a.requester_name}</span> · Approver: <span className="text-slate-800 font-semibold">{a.approver_name}</span> · Expires {formatDate(a.expires_at)}
                    </div>
                    {a.comment && <div className="text-xs text-slate-600 mt-2 rounded bg-slate-50 border border-slate-200 p-2">Approver note: {a.comment}</div>}
                  </div>
                  {iAmApprover && (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setDecidingId(a.id); setDecision("Approved"); }} data-testid={`acc-approve-${a.id.slice(0, 8)}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50" onClick={() => { setDecidingId(a.id); setDecision("ChangesRequested"); }}>Request changes</Button>
                      <Button size="sm" variant="outline" className="border-rose-300 text-rose-800 hover:bg-rose-50" onClick={() => { setDecidingId(a.id); setDecision("Rejected"); }} data-testid={`acc-reject-${a.id.slice(0, 8)}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {decidingId && (
        <Dialog open={true} onOpenChange={(o) => !o && setDecidingId(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Confirm decision — {decision}</DialogTitle></DialogHeader>
            <Textarea rows={3} placeholder="Optional comment" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="acc-comment" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDecidingId(null)}>Cancel</Button>
              <Button onClick={decide} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="acc-confirm">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
